import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@material-ui/core/styles';
import { setNumberFormat } from 'lib/commonLib';

import { getMediaTargetParRank } from 'lib/api/media';
import CurrentStatus from 'components/atoms/CurrentStatus';
import ContentsWrap from 'components/molecules/ContentsWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import Highchart from 'components/organisms/Chart/Highchart';
import SwitchingArea from 'components/organisms/SwitchingArea';

const TargetParRankGraph = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    params,
    handleClickSnackbar = (f) => f,
    infoMessage = t('까지의 통계 그래프입니다.', {
      date: t('term', {
        startDate: moment(params.sDate).format('YYYY-MM-DD'),
        endDate: moment(params.eDate).format('YYYY-MM-DD'),
      }),
    }),
    title = '지면효율등급별',
    reFetch,
  } = props;

  const rankGraphHead = [
    'L1',
    'L2',
    'L3',
    'L4',
    'L5',
    'L6',
    'L7',
    'L8',
    'L9',
    'L10',
    'L11',
  ];
  const initOptions = {
    roasGraph: {
      chart: { type: 'column', backgroundColor: theme.palette.common.bgColor },
      title: { text: t('등급별 총 ROAS'), style: { fontWeight: 'bold' } },
      credits: { enabled: false },
      legend: { enabled: true },
      xAxis: {
        type: 'category',
        plotLines: [
          {
            value: 1421,
            color: 'red',
            width: 1,
            label: { align: 'center', style: { color: 'gray' } },
            zIndex: 10000,
          },
        ],
        categories: [
          'L1',
          'L2',
          'L3',
          'L4',
          'L5',
          'L6',
          'L7',
          'L8',
          'L9',
          'L10',
          'L11',
        ],
      },
    },
    convGraph: {
      chart: { type: 'column', backgroundColor: theme.palette.common.bgColor },
      title: {
        text: t('등급별 전환 단가 + 전환율'),
        style: { fontWeight: 'bold' },
      },
      credits: { enabled: false }, // remove highChart Logo
      xAxis: { categories: rankGraphHead },
      yAxis: [
        {
          min: 0,
          title: {
            text: '(' + t('전환 단가') + ')',
            align: 'high',
            textAlign: 'left',
            rotation: 0,
            offset: 0,
            margin: 0,
            y: -10,
            x: -65,
          },
          plotLines: [],
          labels: { format: '{value:.,0f}' },
        },
        {
          opposite: true,
          min: 0,
          title: {
            text: '(' + t('전환율') + ')',
            align: 'high',
            textAlign: 'left',
            rotation: 0,
            offset: 0,
            margin: 0,
            y: -10,
            x: 0,
          },
          plotLines: [],
          labels: { format: '{value:.,0f}' },
        },
      ],
      plotOptions: { column: { pointPadding: 0.2, borderWidth: 0 } },
      series: [],
      data: { columns: [] },
    },
    prdtGraph: {
      colors: ['#998fcf', '#f5e6b2', '#ffa1a1', '#5e9590', '#518ce7'],
      chart: { type: 'bar', backgroundColor: theme.palette.common.bgColor },
      credits: { enabled: false }, // remove highChart Logo
      title: {
        text: t('등급별 상품 지출 점유율'),
        style: { fontWeight: 'bold' },
      },
      subtitle: {
        text: '* 순서: 지출 금액(점유율) / 상품 개수 ',
        align: 'right',
        verticalAlign: 'bottom',
        y: 0,
      },
      xAxis: { categories: rankGraphHead },
      yAxis: { min: 0, title: { text: '' }, reversed: true },
      tooltip: { shared: true },
      plotOptions: { bar: { stacking: 'percent' } },
      // series: obj.series,
      series: [],
      exporting: { enabled: false },
    },
    reqGraph: {
      chart: { type: 'column', backgroundColor: theme.palette.common.bgColor },
      title: {
        text: t('요청수 대비 사용수'),
        style: { fontWeight: 'bold' },
      },
      credits: { enabled: false }, // remove highChart Logo
      xAxis: { categories: rankGraphHead },
      legend: { alignColumns: false },
      yAxis: [
        {
          min: 0,
          title: {
            text: '(' + t('백만') + ')',
            align: 'high',
            textAlign: 'left',
            rotation: 0,
            offset: 0,
            margin: 0,
            y: -10,
            x: -45,
          },
          plotLines: [],
          labels: {
            format: '{value:.,0f}',
            formatter: function () {
              return setNumberFormat(this.value / 1000000);
            },
          },
        },
      ],
      plotOptions: {
        column: { pointPadding: 0.2, borderWidth: 0 },
        series: { colorByPoint: true },
      },
      series: [],
      data: { columns: [] },
    },
  };

  const [status, setStatus] = useState('loading');
  const [optionList, setOptionList] = useState(initOptions);
  const [prevParams, setPrevParams] = useState({});

  useEffect(() => {
    if (JSON.stringify(params) !== JSON.stringify(prevParams) || reFetch) {
      getGraphList();
    }
    // eslint-disable-next-line
  }, [params, reFetch]);

  const getGraphList = () => {
    setStatus('loading');
    getMediaTargetParRank({ params: params })
      .then((response) => {
        const { data } = response.data;
        let newOptions = initOptions;
        let advrtsAmt = [];
        let AvgAdvrtsAmt = [];
        for (let key in data) {
          const newDataKey = Object.keys(data[key]);
          let newData = [];
          if (key === 'roasGraph') {
            let avg = 0;
            const colors = [
              '#ff2000',
              '#ff7b00',
              '#f52393',
              '#800080',
              '#0062c6',
              '#00b1d4',
              '#00ab00',
              '#8fe200',
              '#ffcf00',
              '#8000ff',
              '#593110',
            ];
            for (let i = 0; i < 11; i++) {
              let newObj = {
                color: colors[i],
              };
              for (let objKey of newDataKey) {
                if (objKey === 'PAR_RNK') {
                  newObj['name'] = 'L' + data[key][objKey]['data'][i]['val'];
                } else if (objKey === 'ROAS') {
                  newObj['y'] = data[key][objKey]['data'][i]['val'] *= 100;
                  avg = avg + data[key][objKey]['data'][i]['val'];
                } else if (objKey === 'ADVRTS_AMT') {
                  advrtsAmt.push(data[key][objKey]['data'][i]['val']);
                  newObj[objKey] = data[key][objKey]['data'][i]['val'];
                } else if (objKey === 'AVG_ADVRTS_AMT') {
                  AvgAdvrtsAmt.push(data[key][objKey]['data'][i]['val']);
                  newObj[objKey] = data[key][objKey]['data'][i]['val'];
                } else {
                  newObj[objKey] = data[key][objKey]['data'][i]['val'];
                }
              }
              newData.push(newObj);
            }
            newOptions[key] = {
              ...newOptions[key],
              yAxis: [
                {
                  min: 0,
                  title: {
                    text: '(' + t('총 ROAS') + ')',
                    align: 'high',
                    textAlign: 'left',
                    rotation: 0,
                    offset: 0,
                    margin: 0,
                    y: -10,
                    x: -60,
                  },
                  plotLines: [
                    {
                      dashStyle: 'Dot',
                      value: avg / data[key][newDataKey[0]]['data'].length,
                      color: 'purple',
                      width: 1,
                      label: {
                        text: t('평균 ROAS'),
                        align: 'right',
                        style: { color: 'purple' },
                      },
                    },
                  ],
                  labels: { format: '{value:.,0f}' },
                  index: 0,
                },
                {
                  opposite: true,
                  min: 0,
                  title: {
                    text: '(' + t('백만') + ')',
                    align: 'high',
                    textAlign: 'right',
                    rotation: 0,
                    offset: 0,
                    margin: 0,
                    y: -10,
                    x: 47,
                  },
                  labels: {
                    format: '{value:.,0f}',
                    formatter: function () {
                      return setNumberFormat(this.value / 1000000);
                    },
                  },
                  left: 65,
                },
                {
                  opposite: true,
                  min: 0,
                  title: {
                    text: '(' + t('원') + ')',
                    align: 'high',
                    textAlign: 'right',
                    rotation: 0,
                    offset: 0,
                    margin: 0,
                    y: -10,
                    x: 35,
                  },
                  labels: { format: '{value:.,0f}' },
                  left: 57,
                },
              ],
              series: [
                {
                  name: t('등급별 총 ROAS'),
                  data: newData,
                  showInLegend: false,
                },
                {
                  yAxis: 1,
                  type: 'spline',
                  name: t('지출금액'),
                  data: advrtsAmt,
                  color: '#ff2000',
                },
                {
                  yAxis: 2,
                  type: 'spline',
                  name: t('평균 지출'),
                  data: AvgAdvrtsAmt,
                  color: '#00ab00',
                },
              ],
              tooltip: {
                formatter: function () {
                  let sHtml = '<b>' + this.points[0].key + '</b>'; // L1, L2, ... 등
                  const i = this.x.split('L')[1] - 1;
                  sHtml +=
                    '<br><b>' +
                    t('총 지출금액') +
                    ': </b>' +
                    setNumberFormat(advrtsAmt[i]) +
                    t('원') +
                    '<br><b>' +
                    t('평균 총 지출금액') +
                    ': </b>' +
                    setNumberFormat(this.points[0].point.MEDIA_AVG_ADVRTS_AMT) +
                    t('원') +
                    '<br><b>' +
                    t('평균 지출') +
                    ': </b>' +
                    setNumberFormat(AvgAdvrtsAmt[i], 'integer', '0,0.0') +
                    t('원') +
                    '<br><b>' +
                    t('지면 개수') +
                    ': </b>' +
                    setNumberFormat(this.points[0].point.MEDIA_SCRIPT_CNT) +
                    t('개') +
                    '<br><b>' +
                    t('총 ROAS') +
                    ': </b>' +
                    setNumberFormat(this.y, 'percent');
                  return sHtml;
                },
                shared: true,
              },
            };
          } else if (key === 'convGraph') {
            let clickRateOption = { valueSuffix: '%', data: [] };
            let convAmtData = [];
            let convRateData = [];
            for (let i = 0; i < 11; i++) {
              for (let objKey of newDataKey) {
                if (objKey === 'CLICK_RATE') {
                  clickRateOption['data'].push(
                    (data[key][objKey]['data'][i]['val'] *= 100)
                  );
                } else if (objKey === 'CONV_AMT') {
                  convAmtData.push(data[key][objKey]['data'][i]['val']);
                } else if (objKey === 'CONV_RATE') {
                  convRateData.push(
                    (data[key][objKey]['data'][i]['val'] *= 100)
                  );
                }
              }
            }
            newOptions[key] = {
              ...newOptions[key],
              series: [
                {
                  name: t('전환 단가'),
                  data: convAmtData,
                  tooltip: { decimals: 0, valueSuffix: '원' },
                },
                {
                  yAxis: 1,
                  name: t('전환율'),
                  data: convRateData,
                  tooltip: { decimals: 0, valueSuffix: '%' },
                },
              ],
              tooltip: {
                formatter: function () {
                  let idx = this['x'].replace('L', '') - 1;
                  let sHtml = '<b>' + this.points[0].key + '</b>'; // L1, L2, ... 등
                  let clickRateValue = !!clickRateOption['data'][idx]
                    ? setNumberFormat(
                        clickRateOption['data'][idx],
                        'integer',
                        '0,0.000'
                      )
                    : 0;
                  sHtml +=
                    '<br><b>' +
                    t('클릭률') +
                    ': </b>' +
                    clickRateValue +
                    clickRateOption['valueSuffix'];
                  sHtml += this.points.reduce(function (s, point) {
                    let yValue = '';
                    if (point['colorIndex'] === 0) {
                      yValue = setNumberFormat(point.y);
                    } else {
                      yValue = setNumberFormat(point.y, 'integer', '0,0.00');
                    }
                    return (
                      s +
                      '<br><b>' +
                      point.series.name +
                      ': </b>' +
                      yValue +
                      point.series.options.tooltip.valueSuffix
                    );
                  }, '');
                  return sHtml;
                },
                shared: true,
              },
            };
          } else if (key === 'prdtGraph') {
            const targetList = ['BANNER', 'ICOVER', 'BRAND', 'NATIVE', 'PLUS'];
            for (let objKey of targetList) {
              let newObj = {};
              let newArray = [];
              switch (objKey) {
                case 'BANNER':
                  newObj['name'] = t('배너');
                  break;
                case 'ICOVER':
                  newObj['name'] = t('아이커버');
                  break;
                case 'BRAND':
                  newObj['name'] = t('브랜드');
                  break;
                case 'PLUS':
                  newObj['name'] = t('플러스콜');
                  break;
                case 'NATIVE':
                  newObj['name'] = t('네이티브');
                  break;
                default:
                  newObj['name'] = t('알수없음');
              }

              for (let i = 0; i < 11; i++) {
                newArray.push({
                  y: data[key][objKey + '_ADVRTS_AMT']['data'][i]['val'],
                  ADVRTS_RATE:
                    data[key][objKey + '_ADVRTS_RATE']['data'][i]['val'],
                  PAR_CNT: data[key][objKey + '_PAR_CNT']['data'][i]['val'],
                });
              }
              newObj['data'] = newArray;
              newData.push(newObj);
            }
            newOptions[key] = {
              ...newOptions[key],
              series: newData,
              tooltip: {
                formatter: function () {
                  let idx = this['x'].replace('L', '') - 1;
                  let totalAdvrtsAmt = 0;
                  let totalParCnt = 0;
                  for (let item of this.points) {
                    totalAdvrtsAmt = totalAdvrtsAmt + item.y;
                    totalParCnt = totalParCnt + item.point.PAR_CNT;
                  }
                  let sHtml = '<b>' + this.points[0].key + '</b>'; // L1, L2, ... 등
                  sHtml +=
                    '<br><b>' +
                    t('총 지출금액') +
                    ': ' +
                    setNumberFormat(totalAdvrtsAmt) +
                    t('원') +
                    '</b> / ' +
                    setNumberFormat(totalParCnt) +
                    t('개');
                  sHtml += this.points.reduce(function (s, point) {
                    const lData = point.series.userOptions.data[idx];
                    return (
                      s +
                      '<br><b>' +
                      point.series.name +
                      ': ' +
                      setNumberFormat(lData['y']) +
                      '원' +
                      '</b>' +
                      '(' +
                      setNumberFormat(lData['ADVRTS_RATE'], 'percent') +
                      ') / ' +
                      setNumberFormat(lData['PAR_CNT']) +
                      t('개')
                    );
                  }, '');
                  return sHtml;
                },
                shared: true,
              },
            };
          } else if (key === 'reqGraph') {
            let reqCnt = [];
            let reqUsedCnt = [];
            let reqUsedRate = [];
            for (let i = 0; i < 11; i++) {
              for (let objKey of newDataKey) {
                if (objKey === 'REQ_CNT') {
                  reqCnt.push({
                    y: data[key][objKey]['data'][i]['val'],
                    color: 'rgba(255,255,255,0)',
                  });
                } else if (objKey === 'REQ_USED_CNT') {
                  reqUsedCnt.push(data[key][objKey]['data'][i]['val']);
                } else if (objKey === 'REQ_USED_RATE') {
                  reqUsedRate.push(
                    (data[key][objKey]['data'][i]['val'] *= 100)
                  );
                }
              }
            }
            newOptions[key] = {
              ...newOptions[key],
              plotOptions: {
                column: { grouping: false, shadow: false, borderWidth: 0 },
              },
              tooltip: {
                formatter: function () {
                  let idx = this['x'].replace('L', '') - 1;
                  let sHtml = '<b>' + this.points[0].key + '</b>'; // L1, L2, ... 등
                  let clickRateValue = !!reqUsedRate[idx]
                    ? setNumberFormat(reqUsedRate[idx], 'integer', '0,0.00')
                    : 0;
                  sHtml += this.points.reduce(function (s, point) {
                    let yValue = '';
                    if (point['colorIndex'] === 0) {
                      yValue = setNumberFormat(point.y);
                    } else {
                      yValue = setNumberFormat(point.y);
                    }
                    return (
                      s + '<br><b>' + point.series.name + ': </b>' + yValue
                    );
                  }, '');
                  sHtml +=
                    '<br><b>' + t('사용률') + ': </b>' + clickRateValue + '%';
                  return sHtml;
                },
                shared: true,
              },
              series: [
                {
                  name: t('요청수'),
                  dashStyle: 'longdash',
                  borderWidth: 1,
                  borderColor: 'rgb(124, 181, 236)',
                  data: reqCnt,
                },
                {
                  name: t('사용수'),
                  color: '#00',
                  data: reqUsedCnt,
                },
              ],
            };
          }
        }
        setStatus('');
        setOptionList(newOptions);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: { type: 'error' },
        });
        setStatus('error');
      });
    setPrevParams(params);
  };

  return (
    <SwitchingArea
      title={
        <InfoTitleArea
          title={title}
          tooltipTitle={infoMessage}
          iconType="fainfoCircle"
          iconColor="graph"
        />
      }
      isOpen={false}
    >
      {status !== '' ? (
        <CurrentStatus type={status} />
      ) : (
        <>
          <ContentsWrap contentsCount={4}>
            <Highchart options={optionList['roasGraph']} />
            <Highchart options={optionList['convGraph']} />
            <Highchart
              options={optionList['prdtGraph']}
              helperText={{ align: 'right' }}
            />
            <Highchart options={optionList['reqGraph']} />
          </ContentsWrap>
          <p style={{ marginTop: 0, textAlign: 'right' }}>
            *{' '}
            {t(
              '위 그래프는 선택한 종료일 전일을 시작으로 90일전 데이터 통계를 통한 결과가 조회됩니다.'
            )}
          </p>
        </>
      )}
    </SwitchingArea>
  );
};

const isEqual = (prefProps, nextProps) => {
  let equal = JSON.stringify(prefProps) !== JSON.stringify(nextProps);
  return !equal;
};

export default React.memo(TargetParRankGraph, isEqual);
